// src/products/userInvites/InviteModal.js
// Purpose: Modal component for sending a new user invite.

import React, { useState } from 'react';
import { userInvitesApi } from './api/userInvitesApi';

const InviteModal = ({ isOpen, onClose, onInviteSent, inviter, context }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [product, setProduct] = useState(context?.product || 'dataroom');
  const [loading, setLoading] = useState(false);

  const buildEmailContent = (link) => {
    const baseGreeting = `Hi,`;
    const templates = {
      dataroom: {
        subject: 'You have been invited to a Dataroom on Kapitalized',
        body: `${baseGreeting}\n\nYou've been invited to access a Dataroom. Click the secure sign-in link to view documents and collaborate:\n\n${link}\n\nIf you weren’t expecting this, you can ignore this email.`
      },
      capraise: {
        subject: 'Invitation to view Capital Raising on Kapitalized',
        body: `${baseGreeting}\n\nYou’ve been invited to view a Capital Raising workspace. Use the secure sign-in link to review details and participate:\n\n${link}\n\nIf you weren’t expecting this, you can ignore this email.`
      },
      equity: {
        subject: 'Invitation to Equity on Kapitalized',
        body: `${baseGreeting}\n\nYou've been invited to access Equity. Sign in securely using this link:\n\n${link}`
      },
      questions: {
        subject: 'Invitation to Questions module on Kapitalized',
        body: `${baseGreeting}\n\nYou’ve been invited to collaborate in Questions. Sign in with this secure link:\n\n${link}`
      },
      default: {
        subject: 'Your secure sign-in link to Kapitalized',
        body: `${baseGreeting}\n\nUse this secure link to sign in:\n\n${link}`
      }
    };
    const key = (context?.product || 'default');
    return templates[key] || templates.default;
  };

  const handleInvite = async () => {
    setLoading(true);
    try {
      const result = await userInvitesApi.createInvite({
        email,
        inviteforproduct: product,
        projectid: context?.projectid,
        roundid: context?.roundid,
        role,
        invitedby: inviter.id,
      });
      const magicLink = result?.magic_link_url || result?.redirect_to;
      if (magicLink) {
        const { subject, body } = buildEmailContent(magicLink);
        const mailto = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        window.location.href = mailto;
      }
      onInviteSent?.();
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <h2 className="text-xl font-semibold mb-2">Invite a User</h2>
        <label>Email:</label>
        <input type="email" className="block w-full mb-3 border rounded px-2 py-1" value={email} onChange={e => setEmail(e.target.value)} />
        <label>Role:</label>
        <select className="block w-full mb-3 border rounded px-2 py-1" value={role} onChange={e => setRole(e.target.value)}>
          <option value="viewer">Viewer</option>
          {/* Support level is not yet functional - hidden from end users */}
          {/* <option value="support">Support</option> */}
          {/* Creator role cannot be assigned via invite - users must subscribe to become Creators */}
          {/* <option value="creator">Creator</option> */}
        </select>
        <label>Product:</label>
        <select className="block w-full mb-3 border rounded px-2 py-1" value={product} onChange={e => setProduct(e.target.value)}>
          <option value="dataroom">Dataroom</option>
          <option value="capraise">CapRaise</option>
          <option value="equity">Equity</option>
          <option value="questions">Questions</option>
        </select>
        <div className="flex justify-end space-x-2 mt-4">
          <button onClick={onClose} className="bg-gray-200 px-3 py-1 rounded">Cancel</button>
          <button onClick={handleInvite} className="bg-blue-600 text-white px-4 py-1 rounded" disabled={loading}>
            {loading ? 'Inviting...' : 'Send Invite'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InviteModal;
